/**
 * Quiz API Service
 * Handles all backend communication for KBC quiz functionality
 * 
 * TEMPORARY - FOR EVENT ONLY
 * This file can be safely deleted when removing the quiz feature
 */

import { getAuthHeaders } from './authHeaders';

// Backend base URL - consistent with main api.ts
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

async function jsonHeaders(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json', ...(await getAuthHeaders()) };
}

export interface QuizAttempt {
  id: string;
  user_email: string;
  display_name: string;
  started_at: string;
  completed_at?: string;
  last_activity_at: string;
  current_question_index: number;
  total_questions: number;
  correct_answers: number;
  incorrect_answers: number;
  time_taken_seconds: number;
  final_score?: number;
  prize_won?: string;
  prize_level?: number;
  is_completed: boolean;
  cashed_out: boolean;
  walked_out: boolean;
  answers_data?: string;
  metadata?: string;
  created_at: string;
  updated_at: string;
}

export interface StartAttemptResponse {
  status: 'new' | 'existing' | 'completed';
  attempt_id: string;
  message: string;
  attempt: QuizAttempt;
}

export interface LeaderboardEntry {
  rank: number;
  user_email: string;
  display_name: string;
  correct_answers: number;
  time_taken_seconds: number;
  final_score: number;
  prize_won?: string;
  prize_level?: number;
  completed_at: string;
  status?: string; // 'Completed', 'Incomplete', 'Walked Out', 'Cashed Out'
  is_completed?: boolean;
  walked_out?: boolean;
  cashed_out?: boolean;
}

export interface QuizStats {
  total_attempts: number;
  completed_attempts: number;
  active_attempts: number;
  average_score: number;
  cash_outs: number;
  walk_outs: number;
}

/**
 * Start a new quiz attempt or retrieve existing attempt
 */
export const startQuizAttempt = async (
  userEmail: string,
  displayName: string,
  totalQuestions: number = 20
): Promise<StartAttemptResponse> => {
  try {
    console.log('🌐 API: Starting quiz attempt for', userEmail);
    console.log('🌐 API: URL:', `${API_BASE_URL}/kbc/start-attempt`);
    
    const response = await fetch(`${API_BASE_URL}/kbc/start-attempt`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        user_email: userEmail,
        display_name: displayName,
        total_questions: totalQuestions,
      }),
    });

    console.log('📡 API: Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ API: Error response:', errorData);
      throw new Error(errorData.detail || `Failed to start quiz attempt: ${response.statusText}`);
    }

    const data: StartAttemptResponse = await response.json();
    console.log('✅ API: Success response:', data);
    console.log('   attempt_id:', data.attempt_id);
    console.log('   attempt.id:', data.attempt.id);
    return data;
  } catch (error) {
    console.error('❌ API: Error starting quiz attempt:', error);
    throw error;
  }
};

/**
 * Save progress for a question
 */
export const saveQuizProgress = async (
  attemptId: string,
  questionIndex: number,
  questionId: string,
  selectedAnswer: string,
  isCorrect: boolean,
  timeTakenSeconds: number
): Promise<QuizAttempt> => {
  try {
    console.log(`🌐 API: Saving progress Q${questionIndex + 1}, attemptId:`, attemptId, 'correct:', isCorrect);
    
    const response = await fetch(`${API_BASE_URL}/kbc/save-progress`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        attempt_id: attemptId,
        question_index: questionIndex,
        question_id: questionId,
        selected_answer: selectedAnswer,
        is_correct: isCorrect,
        time_taken_seconds: timeTakenSeconds,
      }),
    });

    console.log('📡 API: Save progress response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ API: Save progress error:', errorData);
      throw new Error(errorData.detail || `Failed to save progress: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('✅ API: Progress saved, correct_answers now:', data.attempt?.correct_answers);
    return data.attempt;
  } catch (error) {
    console.error('❌ API: Error saving quiz progress:', error);
    throw error;
  }
};

/**
 * Complete a quiz attempt
 */
export const completeQuizAttempt = async (
  attemptId: string,
  finalScore: number,
  prizeWon?: string,
  prizeLevel?: number,
  cashedOut: boolean = false,
  walkedOut: boolean = false,
  totalTimeSeconds?: number
): Promise<QuizAttempt> => {
  try {
    console.log('🌐 API: Completing quiz attempt');
    console.log('   attemptId:', attemptId);
    console.log('   finalScore:', finalScore);
    console.log('   cashedOut:', cashedOut);
    console.log('   walkedOut:', walkedOut);
    
    const response = await fetch(`${API_BASE_URL}/kbc/complete-attempt`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        attempt_id: attemptId,
        final_score: finalScore,
        prize_won: prizeWon,
        prize_level: prizeLevel,
        cashed_out: cashedOut,
        walked_out: walkedOut,
        total_time_seconds: totalTimeSeconds,
      }),
    });

    console.log('📡 API: Complete attempt response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ API: Complete attempt error:', errorData);
      throw new Error(errorData.detail || `Failed to complete quiz: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('✅ API: Quiz completed! is_completed:', data.attempt?.is_completed, 'correct_answers:', data.attempt?.correct_answers);
    return data.attempt;
  } catch (error) {
    console.error('❌ API: Error completing quiz attempt:', error);
    throw error;
  }
};

/**
 * Get leaderboard
 */
export const getLeaderboard = async (
  limit: number = 50,
  includeIncomplete: boolean = false
): Promise<LeaderboardEntry[]> => {
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      include_incomplete: includeIncomplete.toString(),
    });

    const response = await fetch(`${API_BASE_URL}/kbc/leaderboard?${params}`, {
      method: 'GET',
      headers: await jsonHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch leaderboard: ${response.statusText}`);
    }

    const data = await response.json();
    // Backend returns {leaderboard: [], count: n}, extract the array
    return data.leaderboard || data;
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    throw error;
  }
};

/**
 * Get user's quiz attempt
 */
export const getUserAttempt = async (userEmail: string): Promise<QuizAttempt | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/kbc/user-attempt/${encodeURIComponent(userEmail)}`, {
      method: 'GET',
      headers: await jsonHeaders(),
    });

    if (response.status === 404) {
      return null; // No attempt found
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch user attempt: ${response.statusText}`);
    }

    const data = await response.json();
    // Backend returns {attempt: {...}, has_attempt: bool}, extract the attempt
    return data.attempt;
  } catch (error) {
    console.error('Error fetching user attempt:', error);
    throw error;
  }
};

/**
 * Get user's rank
 */
export const getUserRank = async (userEmail: string): Promise<{ rank: number; total_players: number } | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/kbc/user-rank/${encodeURIComponent(userEmail)}`, {
      method: 'GET',
      headers: await jsonHeaders(),
    });

    if (response.status === 404) {
      return null; // User not found
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch user rank: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching user rank:', error);
    throw error;
  }
};

/**
 * Get quiz statistics
 */
export const getQuizStats = async (): Promise<QuizStats> => {
  try {
    const response = await fetch(`${API_BASE_URL}/kbc/stats`, {
      method: 'GET',
      headers: await jsonHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch stats: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching quiz stats:', error);
    throw error;
  }
};

export interface AttemptsRemainingResponse {
  user_email: string;
  max_attempts: number;
  completed_attempts: number;
  remaining_attempts: number;
  can_attempt: boolean;
}

/**
 * Get number of remaining attempts for a user
 */
export const getAttemptsRemaining = async (userEmail: string): Promise<AttemptsRemainingResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/kbc/attempts-remaining/${encodeURIComponent(userEmail)}`, {
      method: 'GET',
      headers: await jsonHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get attempts remaining: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting attempts remaining:', error);
    throw error;
  }
};

// ============================================================================
// ADMIN API METHODS
// ============================================================================

export interface AdminAttemptFilter {
  limit?: number;
  offset?: number;
  user_email_filter?: string;
  is_completed_filter?: boolean;
  order_by?: string;
  order_desc?: boolean;
}

export interface AdminUser {
  user_email: string;
  display_name: string;
  total_attempts: number;
  completed_attempts: number;
  last_attempt_at: string;
}

/**
 * Get all quiz attempts (admin only)
 */
export const adminGetAllAttempts = async (
  adminEmail: string,
  filters: AdminAttemptFilter = {}
): Promise<QuizAttempt[]> => {
  try {
    const requestBody = {
      admin_email: adminEmail,
      ...filters,
    };
    
    console.log('🔍 quizApi: Calling /kbc/admin/get-all-attempts');
    console.log('📤 quizApi: Request body:', requestBody);
    console.log('🌐 quizApi: URL:', `${API_BASE_URL}/kbc/admin/get-all-attempts`);
    
    const response = await fetch(`${API_BASE_URL}/kbc/admin/get-all-attempts`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify(requestBody),
    });

    console.log('📥 quizApi: Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ quizApi: Error response:', errorData);
      throw new Error(errorData.detail || `Failed to fetch attempts: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('📦 quizApi: Raw response data:', data);
    console.log('📊 quizApi: Attempts array:', data.attempts);
    console.log('📈 quizApi: Attempts count:', data.attempts?.length || 0);
    
    // Return the attempts array, or empty array if undefined
    const attempts = data.attempts || [];
    console.log('✅ quizApi: Returning attempts:', attempts.length, 'records');
    
    return attempts;
  } catch (error) {
    console.error('❌ quizApi: Error fetching all attempts:', error);
    throw error;
  }
};

/**
 * Get detailed attempt information (admin only)
 */
export const adminGetAttemptDetails = async (
  adminEmail: string,
  attemptId: string
): Promise<QuizAttempt> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/kbc/admin/attempt-details/${attemptId}?admin_email=${encodeURIComponent(adminEmail)}`,
      {
        method: 'GET',
        headers: await jsonHeaders(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch attempt details: ${response.statusText}`);
    }

    const data = await response.json();
    return data.attempt;
  } catch (error) {
    console.error('Error fetching attempt details:', error);
    throw error;
  }
};

/**
 * Delete all attempts for a user (admin only)
 */
export const adminDeleteUserAttempts = async (
  adminEmail: string,
  userEmail: string
): Promise<{ success: boolean; deleted_count: number; message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/kbc/admin/delete-user-attempts`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
        user_email: userEmail,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to delete user attempts: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting user attempts:', error);
    throw error;
  }
};

/**
 * Delete a specific attempt (admin only)
 */
export const adminDeleteAttempt = async (
  adminEmail: string,
  attemptId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/kbc/admin/delete-attempt`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
        attempt_id: attemptId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to delete attempt: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting attempt:', error);
    throw error;
  }
};

/**
 * Update completion status of an attempt (admin only)
 */
export const adminUpdateCompletionStatus = async (
  adminEmail: string,
  attemptId: string,
  isCompleted: boolean
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/kbc/admin/update-completion-status`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
        attempt_id: attemptId,
        is_completed: isCompleted,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to update status: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating completion status:', error);
    throw error;
  }
};

/**
 * Get all users who have attempted the quiz (admin only)
 */
export const adminGetAllUsers = async (adminEmail: string): Promise<AdminUser[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/kbc/admin/all-users?admin_email=${encodeURIComponent(adminEmail)}`,
      {
        method: 'GET',
        headers: await jsonHeaders(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch users: ${response.statusText}`);
    }

    const data = await response.json();
    return data.users;
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
};

/**
 * Export all quiz data (admin only)
 */
export const adminExportData = async (adminEmail: string): Promise<QuizAttempt[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/kbc/admin/export-data?admin_email=${encodeURIComponent(adminEmail)}`,
      {
        method: 'GET',
        headers: await jsonHeaders(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to export data: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error exporting data:', error);
    throw error;
  }
};

// ============================================================================
// PRIZE CONFIG API METHODS
// ============================================================================

export interface PrizeTierConfig {
  level: number;
  label: string;
  value: number;
  enabled: boolean;
}

/**
 * Get current prize tier configuration (public endpoint)
 * This is the source of truth for all prize displays
 */
export const getPrizeConfig = async (): Promise<PrizeTierConfig[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/kbc/get-prize-config`,
      {
        method: 'GET',
        headers: await jsonHeaders(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch prize config: ${response.statusText}`);
    }

    const data = await response.json();
    return data.prize_tiers;
  } catch (error) {
    console.error('Error fetching prize config:', error);
    throw error;
  }
};

/**
 * Get current prize tier configuration (admin only)
 */
export const adminGetPrizeConfig = async (adminEmail: string): Promise<PrizeTierConfig[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/kbc/admin/get-prize-config?admin_email=${encodeURIComponent(adminEmail)}`,
      {
        method: 'GET',
        headers: await jsonHeaders(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to fetch prize config: ${response.statusText}`);
    }

    const data = await response.json();
    return data.prize_tiers;
  } catch (error) {
    console.error('Error fetching prize config:', error);
    throw error;
  }
};

/**
 * Update prize tier configuration (admin only)
 */
export const adminUpdatePrizeConfig = async (
  adminEmail: string,
  prizeTiers: PrizeTierConfig[]
): Promise<{ success: boolean; message: string; prize_tiers: PrizeTierConfig[] }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/kbc/admin/update-prize-config`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
        prize_tiers: prizeTiers,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to update prize config: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating prize config:', error);
    throw error;
  }
};

/**
 * Reset prize tier configuration to defaults (admin only)
 */
export const adminResetPrizeConfig = async (
  adminEmail: string
): Promise<{ success: boolean; message: string; prize_tiers: PrizeTierConfig[] }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/kbc/admin/reset-prize-config`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        admin_email: adminEmail,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to reset prize config: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error resetting prize config:', error);
    throw error;
  }
};

// ============================================================================
// WAITLIST API METHODS
// ============================================================================

export interface WaitlistResponse {
  status: string;
  message: string;
  entry?: any;
}

/**
 * Join the waitlist for CoCaptain feature updates
 */
export const joinWaitlist = async (
  userEmail: string,
  displayName: string,
  source: string = 'kbc_quiz',
  metadata?: Record<string, any>
): Promise<WaitlistResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/waitlist`, {
      method: 'POST',
      headers: await jsonHeaders(),
      body: JSON.stringify({
        user_email: userEmail,
        display_name: displayName,
        source,
        metadata,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to join waitlist: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error joining waitlist:', error);
    throw error;
  }
};

/**
 * Get waitlist count
 */
export const getWaitlistCount = async (): Promise<number> => {
  try {
    const response = await fetch(`${API_BASE_URL}/waitlist/count`, {
      method: 'GET',
      headers: await jsonHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to get waitlist count: ${response.statusText}`);
    }

    const data = await response.json();
    return data.count;
  } catch (error) {
    console.error('Error getting waitlist count:', error);
    throw error;
  }
};
